import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionsApi } from '../api/client';

interface UsageData {
  plan: {
    name: string;
    displayName: string;
  };
  usage: {
    sites: number;
    pages: number;
  };
  limits: {
    maxSites: number;
    maxPagesPerSite: number;
  };
}

export default function UsageLimits() {
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsageData();
  }, []);

  const loadUsageData = async () => {
    try {
      setLoading(true);
      const subscription = await subscriptionsApi.getCurrentSubscription();
      setUsage({
        plan: subscription.plan,
        usage: subscription.usage,
        limits: subscription.limits,
      });
    } catch (error) {
      console.warn('Failed to load usage data, using defaults:', error);
      // Set default free plan usage for demo
      setUsage({
        plan: {
          name: 'FREE',
          displayName: 'Free',
        },
        usage: {
          sites: 0,
          pages: 0,
        },
        limits: {
          maxSites: 1,
          maxPagesPerSite: 5,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!usage || !usage.limits || !usage.usage || !usage.plan) {
    return null;
  }

  const siteUsagePercent = usage.limits.maxSites > 0 
    ? (usage.usage.sites / usage.limits.maxSites) * 100 
    : 0;
  const isNearLimit = siteUsagePercent >= 80;
  const isAtLimit = siteUsagePercent >= 100;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900">
          {usage.plan.displayName} Plan
        </h4>
        {usage.plan.name === 'FREE' && (
          <Link
            to="/pricing"
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            Upgrade
          </Link>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">Sites</span>
            <span className={`font-medium ${isAtLimit ? 'text-red-600' : 'text-gray-900'}`}>
              {usage.usage.sites} / {usage.limits.maxSites}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                isAtLimit
                  ? 'bg-red-600'
                  : isNearLimit
                  ? 'bg-yellow-500'
                  : 'bg-blue-600'
              }`}
              style={{ width: `${Math.min(siteUsagePercent, 100)}%` }}
            />
          </div>
        </div>

        <div className="text-xs text-gray-500">
          <p>Max {usage.limits.maxPagesPerSite} pages per site</p>
          <p>Total pages created: {usage.usage.pages}</p>
        </div>
      </div>

      {isNearLimit && !isAtLimit && (
        <div className="mt-3 p-2 bg-yellow-50 rounded-md">
          <p className="text-xs text-yellow-800">
            You're approaching your site limit. Consider upgrading for more sites.
          </p>
        </div>
      )}

      {isAtLimit && (
        <div className="mt-3 p-2 bg-red-50 rounded-md">
          <p className="text-xs text-red-800">
            You've reached your site limit.{' '}
            <Link to="/pricing" className="font-medium underline">
              Upgrade now
            </Link>{' '}
            to create more sites.
          </p>
        </div>
      )}
    </div>
  );
}